import React from "react"
import { Link } from "gatsby"
import styled from "styled-components"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import { h2Styles, h3Styles, h4Styles, h5Styles } from "@constants/typography"
import ImageHelper, {
  Container as ImageHelperContainer,
} from "@components/ImageHelper"
import LinkButton from "@components/buttons/LinkButton"

const Container = styled.div`
  padding: 48px 20px;

  @media ${breakpoints.laptop} {
    padding: 64px calc(1 / 12 * 100vw);
  }
`

const Content = styled.div`
  @media ${breakpoints.laptop} {
    display: flex;
    justify-content: space-between;
  }
`

const Header = styled.h2`
  ${h4Styles}
  align-items: center;
  justify-content: space-between;
  display: flex;
  margin-bottom: 24px;

  @media ${breakpoints.laptop} {
    ${h3Styles}
    margin-bottom: 48px;
  }

  @media ${breakpoints.desktop} {
    ${h2Styles}
    margin-bottom: 64px;
  }
`

const Column = styled.div`
  flex-wrap: wrap;
  display: flex;
  width: 100%;

  @media ${breakpoints.laptop} {
    width: calc(3.2 / 12 * 100vw);
  }
`

const ExternalLink = styled.a`
  position: relative;
  display: block;
`

const InternalLink = styled(Link)`
  position: relative;
  display: block;
`

const ImageContainer = styled.div`
  width: 100%;
  margin-bottom: 24px;

  @media ${breakpoints.laptop} {
    &:last-child {
      margin-bottom: 0;
    }
  }

  ${ImageHelperContainer} {
    width: 100%;
    height: calc((100vw - 40px));

    @media ${breakpoints.laptop} {
      height: calc(3.2 / 12 * 100vw);
    }
  }
`

const Text = styled.div<{ color?: string }>`
  ${h5Styles};
  z-index: 1;
  position: absolute;
  align-items: center;
  justify-content: center;
  display: flex;
  width: 100%;
  height: 100%;
  font-size: 28px;
  line-height: 36px;
  text-align: center;
  color: ${(props) => props.color || colors.white};

  @media ${breakpoints.desktop} {
    font-size: 32px;
    line-height: 40px;
  }

  @media ${breakpoints.largeDesktop} {
    font-size: 48px;
    line-height: 56px;
  }
`

interface LinkProps {
  link: string
  text: string
  image?: {
    file?: {
      url?: string
    }
  }
  external?: boolean
  openNewTab?: boolean
}

interface PromoBlockProps {
  blockId?: string
  header: string
  links: Array<LinkProps>
  cta?: {
    link: string
    text: string
    external?: boolean
    openNewTab?: boolean
  }
}

const PromoBlock: React.FC<PromoBlockProps> = ({
  blockId,
  header,
  links,
  cta,
}) => {
  if (!links || links.length !== 3) {
    return null
  }

  const renderLink = (link: LinkProps) =>
    link.external ? (
      <ExternalLink
        href={link.link}
        target={link.openNewTab ? "_blank" : undefined}
        rel="noopener"
      >
        <Text>{link.text}</Text>
        <ImageHelper src={link.image?.file?.url || ""} />
      </ExternalLink>
    ) : (
      <InternalLink to={link.link}>
        <Text>{link.text}</Text>
        <ImageHelper src={link.image?.file?.url || ""} />
      </InternalLink>
    )

  return (
    <Container id={blockId || undefined}>
      <Header>
        {header}
        {cta && (
          <LinkButton
            to={cta.link}
            text={cta.text}
            external={cta.external}
            target={cta.openNewTab ? "_blank" : undefined}
          />
        )}
      </Header>
      <Content>
        <Column>
          <ImageContainer>{renderLink(links[0])}</ImageContainer>
        </Column>
        <Column>
          <ImageContainer>{renderLink(links[1])}</ImageContainer>
        </Column>
        <Column>
          <ImageContainer>{renderLink(links[2])}</ImageContainer>
        </Column>
      </Content>
    </Container>
  )
}

export default PromoBlock
